import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { LayerContext } from './layer.js';
import { Icon, type IconName } from './Icon.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from './primitives/dialog.js';

const SIZES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  /* Os formulários do sinistro: dois lado a lado, e o aviso inteiro com o
     trilho de etapas ao lado (`ClaimsScreen`). */
  xl: 'sm:max-w-6xl',
  /* A mesa do sinistro: três colunas lado a lado — os envolvidos, o
     formulário da fase e o movimento. Até a janela, e não além. */
  '2xl': 'sm:max-w-[min(95rem,calc(100vw-2rem))]',
} as const;

export type ModalSize = keyof typeof SIZES;

/**
 * O cabeçalho do diálogo, em dois desenhos.
 *
 * `padrao` é a anatomia do `Card` — título em 13px sobre borda fina, e é o que
 * os quatro diálogos da casa usavam. `destaque` é o do desenho de 17/09: faixa
 * tingida, ladrilho com o ícone e um segundo texto identificando o registro.
 *
 * Ele existe porque o painel de ações **nomeia uma linha da tabela** ("Item
 * #17089"), e não uma tarefa. Quem abriu precisa reconhecer de imediato sobre
 * qual registro vai agir, e isso é trabalho de cabeçalho, não de corpo.
 */
export type ModalTone = 'padrao' | 'destaque';

/**
 * O único diálogo modal do produto — `DESIGN.md` lista "modal como primeira
 * ideia" em "o que não fazemos", e por isso este componente existe uma vez só,
 * aqui, e não um `<div>` fixo desenhado à mão em cada tela que pedir um.
 *
 * As exceções estão registradas no próprio `DESIGN.md`. O caso que as abriu:
 * mudar uma demanda de departamento a partir da MESA (lista), onde não há linha
 * para expandir um formulário inline como o "Aplicar" da situação faz no
 * detalhe — a alternativa real era navegar até o detalhe só para encaminhar, que
 * é o progressivo custando mais cliques que o modal que se queria evitar.
 *
 * A terceira, de 18/09/2026, é o cadastro do comercial, e o que ela ensina é
 * outra coisa: **inline não é gratuito quando a tela é longa.** Os formulários
 * abriam acima de três listas empilhadas, e quem clicava numa linha do fim da
 * página não via nada acontecer.
 *
 * Título é obrigatório: o `DialogPrimitive.Title` do Radix avisa no console
 * quando falta, e "sem título" não é um estado que este componente ofereça.
 *
 * ## Ele CABE na tela, e o corpo é que rola — medido em 10/09
 *
 * O `DialogContent` do shadcn não tem altura máxima nem transbordo: ele é
 * `fixed` centrado por `translate(-50%, -50%)`, então um conteúdo mais alto que
 * a janela não vira barra de rolagem — ele cresce **para os dois lados**. A
 * medição do modal de vendas do ranking, com 31 linhas: 1817px de altura numa
 * janela de 800, começando **508px acima** do topo. As primeiras linhas eram
 * inalcançáveis por rolagem, por teclado e por toque; não havia o que rolar.
 *
 * Os dois modais que existiam antes nunca mostraram isso porque nenhum passava
 * de meia tela. O defeito não era deles: era desta peça, e é aqui que se
 * conserta uma vez.
 *
 * `grid-rows-[auto_minmax(0,1fr)]` é o que permite ao corpo encolher — um
 * `1fr` puro tem mínimo `auto`, que é o tamanho do conteúdo, e o corpo voltaria
 * a empurrar o modal para fora da tela com `overflow-y: auto` ligado e sem
 * efeito nenhum.
 *
 * ## E `min-w-0` no corpo, pela mesma razão na horizontal
 *
 * Item de grid tem largura mínima automática igual ao conteúdo. A tabela de
 * seis colunas media 859px dentro de um modal de 672 — e como o transbordo
 * ficava do lado de fora, quem rolava de lado era **a página**, não a tabela.
 * Com `min-w-0` o corpo encolhe até o modal e a `rolagem-lateral` da tabela
 * volta a ser quem rola, que é o que ela existe para fazer.
 *
 * ## O formulário com etapas cabe aqui dentro
 *
 * O `Form` com `steps` prende o trilho e o rodapé com `sticky`, e quem rola
 * aqui é o corpo — então os dois ficam presos às bordas dele, como ficariam às
 * da página. A faixa de etapas do telefone mede o topo por
 * `--form-steps-top`, que na página desconta o `Header` e aqui é zero.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  descricao,
  size = 'md',
  tone = 'padrao',
  icon,
  leading,
  aside,
  titleAddon,
  belowTitle,
  onEscapeKeyDown,
  dismissOnOutsideClick = true,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  descricao?: ReactNode;
  /** `sm` 24rem · `md` 32rem (padrão) · `lg` 42rem · `xl` 72rem · `2xl` 95rem, só a mesa do sinistro. */
  size?: ModalSize;
  tone?: ModalTone;
  /** Só em `destaque`: o ladrilho à esquerda do título. */
  icon?: IconName;
  /**
   * O que vem ANTES do título — hoje, a seta de voltar do painel de ações
   * quando ele troca de conteúdo no lugar.
   *
   * Ela mora no cabeçalho e não no corpo porque voltar é irmão de fechar: os
   * dois desfazem, e pôr um no topo e o outro no meio faz a pessoa procurar.
   */
  leading?: ReactNode;
  /**
   * O que vem DEPOIS do título, antes do fechar — o bloco do ocorrido na mesa
   * (`DeskModal`). No telefone ele desce para uma linha própria, abaixo do
   * título: lado a lado, o título viraria uma coluna de três letras.
   */
  aside?: ReactNode;
  /**
   * Colado à DIREITA do bloco do título, e não empurrado para a ponta — o
   * analista responsável da mesa do sinistro (07/10/2026). Pequeno: uma
   * pastilha, não um bloco.
   */
  titleAddon?: ReactNode;
  /**
   * Abaixo da descrição, dentro do bloco do título — as etiquetas da mesa.
   * Não vai dentro de `descricao`, que é um `<p>`: selo e botão ali seriam
   * HTML inválido.
   */
  belowTitle?: ReactNode;
  /**
   * Chamado antes de o Esc fechar o diálogo. Quem chamar
   * `event.preventDefault()` fica com o Esc — é como a gaveta da mesa fecha
   * primeiro, e só o segundo Esc fecha o modal.
   */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /**
   * `false`: clicar no véu não fecha. Para o diálogo com formulário longo —
   * o do aviso de sinistro tem dezesseis obrigatórios, e um clique fora
   * apagaria tudo sem perguntar. O X e o Esc continuam fechando: os dois são
   * gestos de quem quis sair.
   */
  dismissOnOutsideClick?: boolean;
  children: ReactNode;
}) {
  const destaque = tone === 'destaque';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onEscapeKeyDown={onEscapeKeyDown}
        onInteractOutside={(event) => {
          if (!dismissOnOutsideClick) event.preventDefault();
        }}
        className={cn(
          'max-h-[calc(100dvh-2rem)] grid-cols-[minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)] rounded-grande border-borda bg-superficie p-0 shadow-cartao',
          SIZES[size],
        )}
      >
        {/* Todo seletor aberto daqui de dentro (calendário, lista) sobe para a
            camada do modal sem precisar declarar — ver `layer.ts`. */}
        <LayerContext.Provider value="modal">
          {/* Mesma anatomia de cabeçalho do `Card` — DESIGN.md: "mesma ação,
            mesma aparência, em toda tela". */}
          <div
            className={cn(
              'flex items-center gap-3',
              destaque
                ? 'rounded-t-grande bg-primaria-suave px-4 py-4'
                : 'flex-col items-stretch gap-0.5 border-b border-borda-suave px-4 pt-3.5 pb-3.5',
              /* Com bloco ao lado, o cabeçalho vira linha a partir do `sm`, e o
               recuo da direita deixa o X do Radix (44px) fora de cima dele. */
              aside && 'gap-2 pr-14 sm:flex-row sm:items-center sm:gap-4',
            )}
          >
            {leading}
            {destaque && icon && (
              /* O ladrilho é BRANCO sobre a faixa tingida, e não o contrário: o
               ícone precisa do mesmo fundo do resto do diálogo para não mudar
               de peso quando a faixa mudar de tom. */
              <span className="flex size-10 shrink-0 items-center justify-center rounded-borda bg-superficie text-primaria-texto">
                <Icon nome={icon} size={20} />
              </span>
            )}
            <div className="min-w-0 flex-1">
              {/* O complemento fica centrado na altura do título e da descrição
                juntos — e o que vem abaixo (`belowTitle`) não o desalinha. */}
              <div className="flex min-w-0 items-center gap-3">
                <div className="min-w-0">
                  <DialogTitle
                    className={cn(
                      'font-bold text-texto',
                      destaque ? 'text-base' : 'text-[0.8125rem]',
                    )}
                  >
                    {title}
                  </DialogTitle>
                  {descricao && (
                    <DialogDescription
                      className={cn(
                        'text-xs',
                        destaque ? 'text-texto-suave' : 'text-texto-suave',
                      )}
                    >
                      {descricao}
                    </DialogDescription>
                  )}
                </div>
                {titleAddon && <div className="min-w-0 shrink">{titleAddon}</div>}
              </div>
              {belowTitle && <div className="mt-2">{belowTitle}</div>}
            </div>
            {aside && <div className="w-full min-w-0 sm:w-auto sm:shrink-0">{aside}</div>}
          </div>
          <div
            className={cn(
              'min-w-0 overflow-y-auto px-4 pb-4 [--form-steps-top:0px] [--form-veil-inset:1rem] [--form-veil:var(--color-superficie)]',
              destaque ? 'pt-4' : 'pt-3.5',
            )}
          >
            {children}
          </div>
        </LayerContext.Provider>
      </DialogContent>
    </Dialog>
  );
}

/**
 * O rodapé de ações de um diálogo — os botões dividem a largura em partes
 * iguais.
 *
 * ## Por que a largura é dividida, e não natural
 *
 * O botão de um diálogo não concorre com nada: o véu já tirou o resto da tela
 * do caminho, e o que sobrou é o que a pessoa veio fazer. Botão de largura
 * natural encostado num canto é o desenho de uma barra de ferramentas, onde
 * ele DIVIDE espaço com outros controles — aqui não divide, e o canto só
 * afasta o alvo de onde os olhos estão.
 *
 * Com dois, a metade para cada é o que impede a assimetria de virar hierarquia
 * sem querer: "Cancelar" e "Encaminhar" têm rótulos de tamanhos diferentes, e
 * na largura natural o mais importante dos dois era, em três dos quatro
 * diálogos da casa, o menor. Quem manda na ênfase é a variante do `Button` —
 * cor e peso —, nunca o comprimento da palavra.
 *
 * ## `grid-flow-col auto-cols-fr`, e não uma contagem em JavaScript
 *
 * Cada filho vira uma coluna de `minmax(0, 1fr)`: um ocupa a linha inteira,
 * dois ficam com metade, três com um terço. A peça não precisa saber quantos
 * são — e o botão que só existe sob condição (`{pode && <Button/>}`) faz os
 * outros redistribuírem sozinhos, que é o caso em que uma contagem escrita à
 * mão erra.
 *
 * `auto-cols-fr` é `minmax(0, 1fr)` e não `1fr`: o mínimo automático de uma
 * coluna é o conteúdo dela, e o `whitespace-nowrap` do `Button` faria um rótulo
 * longo empurrar a coluna vizinha para fora do diálogo.
 */
export function ModalActions({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn('grid auto-cols-fr grid-flow-col gap-2', className)}>
      {children}
    </div>
  );
}
